import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import PropTypes from 'prop-types';
import './CustomAudioPlayer.css';

const CustomAudioPlayer = ({ 
  src, 
  autoPlay = false, 
  preload = "metadata", 
  className = "",
  onLoadStart,
  onLoadedData,
  onEnded,
  onError,
  onTimeUpdate,
  onPlay,
  onPause
}) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSeeking, setIsSeeking] = useState(false);

  // Format time in MM:SS format
  const formatTime = useCallback((timeInSeconds) => {
    if (!timeInSeconds || isNaN(timeInSeconds)) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Calculate progress percentage
  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Determine if we're approaching the end (last 20% of audio)
  const isApproachingEnd = progressPercentage > 80;

  // Handle play/pause toggle
  const togglePlayPause = useCallback(() => {
    if (!audioRef.current || isLoading) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // Clear any previous errors when attempting to play
      setError(null);
      audioRef.current.play().catch((err) => {
        console.error('Error playing audio:', err);
        setError('Failed to play audio');
        setIsPlaying(false);
      });
    }
  }, [isPlaying, isLoading]);

  // Handle seek with range input
  const handleSeek = useCallback((e) => {
    if (!audioRef.current || !duration || isLoading) return;

    try {
      const newTime = Math.max(0, Math.min(duration, parseFloat(e.target.value)));
      audioRef.current.currentTime = newTime;
      if (!isSeeking) {
        setCurrentTime(newTime);
      }
    } catch (err) {
      console.error('Error seeking audio:', err);
    }
  }, [duration, isLoading, isSeeking]);

  // Handle slider input (while dragging)
  const handleSliderInput = useCallback((e) => {
    if (!duration) return;
    const newTime = Math.max(0, Math.min(duration, parseFloat(e.target.value)));
    setCurrentTime(newTime);
  }, [duration]);

  // Handle mouse down on slider
  const handleSeekStart = useCallback(() => {
    setIsSeeking(true);
  }, []);

  // Handle mouse up on slider
  const handleSeekEnd = useCallback(() => {
    setIsSeeking(false);
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((e) => {
    try {
      const newVolume = Math.max(0, Math.min(1, parseFloat(e.target.value)));
      setVolume(newVolume);
      if (audioRef.current) {
        audioRef.current.volume = newVolume;
      }
      setIsMuted(newVolume === 0);
    } catch (err) {
      console.error('Error changing volume:', err);
    }
  }, []);

  // Handle mute toggle
  const toggleMute = useCallback(() => {
    if (!audioRef.current) return;

    try {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    } catch (err) {
      console.error('Error toggling mute:', err);
    }
  }, [isMuted, volume]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadStart = () => {
      setIsLoading(true);
      setError(null);
      onLoadStart?.();
    };

    const handleLoadedData = () => {
      setIsLoading(false);
      setDuration(audio.duration || 0);
      onLoadedData?.();
    };

    const handleLoadedMetadata = () => {
      setIsLoading(false);
      setDuration(audio.duration || 0);
    };

    const handleCanPlay = () => {
      setIsLoading(false);
      if (!duration && audio.duration) {
        setDuration(audio.duration);
      }
    };

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
      onTimeUpdate?.(audio.currentTime);
    };

    const handlePlay = () => {
      setIsPlaying(true);
      setIsLoading(false);
      onPlay?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onEnded?.();
    };

    const handleError = (e) => {
      setError('Error loading audio');
      setIsLoading(false);
      onError?.(e);
    };

    const handleVolumeChange = () => {
      setVolume(audio.volume);
      setIsMuted(audio.volume === 0);
    };

    // Add event listeners
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('volumechange', handleVolumeChange);

    // Check if audio is already ready (for cached audio)
    if (audio.readyState >= 2) { // HAVE_CURRENT_DATA
      setIsLoading(false);
      setDuration(audio.duration || 0);
    }

    // Cleanup
    return () => {
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [onLoadStart, onLoadedData, onTimeUpdate, onPlay, onPause, onEnded, onError, duration, isSeeking]);

  // Handle autoplay
  useEffect(() => {
    if (autoPlay && audioRef.current && !isLoading && !error) {
      audioRef.current.play().catch((err) => {
        console.error('Autoplay failed:', err);
        // Don't set error state for autoplay failures as they're often expected
      });
    }
  }, [autoPlay, isLoading, error]);

  // Fallback timeout to clear loading state if events don't fire
  useEffect(() => {
    if (!src) return;
    
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('Audio loading timeout - clearing loading state');
        setIsLoading(false);
      }
    }, 5000); // 5 second timeout

    return () => clearTimeout(timeout);
  }, [src, isLoading]);

  if (error) {
    return (
      <div className={`custom-audio-player error ${className}`}>
        <div className="error-message">
          <span>⚠️ {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`custom-audio-player ${className}`}>
      <audio
        ref={audioRef}
        src={src}
        preload={preload}
        style={{ display: 'none' }}
      />
      
      <div className="audio-controls">
        <button
          className="play-pause-btn"
          onClick={togglePlayPause}
          disabled={isLoading}
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <div className="loading-spinner" />
          ) : isPlaying ? (
            <FaPause />
          ) : (
            <FaPlay />
          )}
        </button>

        <div className="time-display">
          {formatTime(currentTime)}
        </div>

        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          onChange={handleSeek}
          onInput={handleSliderInput}
          onMouseDown={handleSeekStart}
          onMouseUp={handleSeekEnd}
          onTouchStart={handleSeekStart}
          onTouchEnd={handleSeekEnd}
          className={`audio-slider ${isApproachingEnd ? 'approaching-end' : ''}`}
          disabled={isLoading || !duration}
          aria-label="Audio progress"
          style={{
            background: `linear-gradient(to right, ${isApproachingEnd ? '#f59e0b' : '#3b82f6'} 0%, ${isApproachingEnd ? '#f59e0b' : '#3b82f6'} ${progressPercentage}%, #f1f4f9 ${progressPercentage}%, #f1f4f9 100%)`,
            borderRadius: '2px',
            height: '4px'
          }}
        />

        <div className="time-display">
          {formatTime(duration)}
        </div>

        <div className="volume-controls">
          <button
            className="mute-btn"
            onClick={toggleMute}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? <FaVolumeMute /> : <FaVolumeUp />}
          </button>
          
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="volume-slider"
            aria-label="Volume"
          />
        </div>
      </div>
    </div>
  );
};

CustomAudioPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  autoPlay: PropTypes.bool,
  preload: PropTypes.oneOf(['none', 'metadata', 'auto']),
  className: PropTypes.string,
  onLoadStart: PropTypes.func,
  onLoadedData: PropTypes.func,
  onEnded: PropTypes.func,
  onError: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
};

export default CustomAudioPlayer;